import type { ResultOf } from '@graphql-typed-document-node/core';

import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { HandoverStatusEnum } from '@e-socna/data-layer';

import type { ResponsibilityHandoverForm_HandOverResponsibilities_Mutation } from './hooks/useHandOverResponsibilitiesMutation.hook';

type HandoverReport = ResultOf<
  typeof ResponsibilityHandoverForm_HandOverResponsibilities_Mutation
>['handOverResponsibilities'];

const STATUS_LABELS: Readonly<Record<HandoverStatusEnum, string>> = {
  [HandoverStatusEnum.Planned]: 'Prévu',
  [HandoverStatusEnum.Done]: 'Fait',
  [HandoverStatusEnum.AlreadyDone]: 'Déjà fait',
  [HandoverStatusEnum.Refused]: 'Refusé',
};

const ENTITY_TYPE_LABELS: Readonly<Record<string, string>> = {
  Folder: 'Dossier',
  Mission: 'Mission',
  BusinessEvent: 'Événement métier',
};

interface Props {
  readonly report: HandoverReport;
}

export const HandoverReportTable = ({ report }: Readonly<Props>) => (
  <>
    <Alert
      severity={
        report.membership === HandoverStatusEnum.Refused ? 'error' : 'info'
      }
      sx={{ mt: 2 }}
    >
      Rôles de l&apos;adhésion : {STATUS_LABELS[report.membership]}
    </Alert>
    {!report.entries.length ? (
      <Typography sx={{ mt: 2 }} variant="body2">
        Aucun dossier, mission ou événement métier ouvert où la personne
        représente l&apos;organisation.
      </Typography>
    ) : (
      <Table aria-label="Compte rendu de la passation" size="small">
        <TableHead>
          <TableRow>
            <TableCell>Type</TableCell>
            <TableCell>Code</TableCell>
            <TableCell>Titre</TableCell>
            <TableCell>Statut</TableCell>
            <TableCell>Raison</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {report.entries.map(
            ({ entityType, entityId, code, title, status, reason }) => (
              <TableRow key={entityId}>
                <TableCell>
                  {ENTITY_TYPE_LABELS[entityType] ?? entityType}
                </TableCell>
                <TableCell>{code}</TableCell>
                <TableCell>{title}</TableCell>
                <TableCell>{STATUS_LABELS[status]}</TableCell>
                <TableCell>{reason}</TableCell>
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>
    )}
  </>
);
